import { Badge } from "@/components/ui/badge"
import { formatRentMoveInDate, type RentPeriod } from "@/components/rent/rent-constants"
import { formatAmount, formatSurface } from "@/lib/utils"
import { cn } from "@/lib/utils"
import type { Facility, UnitType } from "@/types/warehouse"

type SummaryTileProps = {
  badge: string
  value: string
  secondaryValue?: string
  className?: string
}

function SummaryTile({ badge, value, secondaryValue, className }: SummaryTileProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-[8px] bg-background p-4 min-h-[120px] justify-between",
        className,
      )}
    >
      <Badge className="h-auto w-fit rounded-[2px] bg-white px-1 py-0.5 text-[12px] leading-4 font-medium text-light-muted">
        {badge}
      </Badge>
      <div className="flex flex-col gap-1">
        <p className="text-[32px] leading-[36px] font-semibold text-foreground">{value}</p>
        {secondaryValue ? (
          <p className="text-[16px] leading-5 text-light-muted line-through">{secondaryValue}</p>
        ) : null}
      </div>
    </div>
  )
}

type RentPaymentSummaryProps = {
  facility?: Facility
  unitType?: UnitType
  startedAt: string
  period: RentPeriod | ""
  totalPrice?: number | null
  totalBefore?: number | null
  compact?: boolean
  footer?: React.ReactNode
}

export function RentPaymentSummary({
  facility,
  unitType,
  startedAt,
  period,
  totalPrice = null,
  totalBefore = null,
  compact = false,
  footer,
}: RentPaymentSummaryProps) {
  const displayName =
    facility?.properties?.find((property) => property.code === "display_name")?.value ??
    facility?.name ??
    "—"

  const hasDiscount =
    totalPrice != null && totalBefore != null && totalBefore > totalPrice

  return (
    <div className={cn("flex h-full flex-col", compact ? "gap-4" : "gap-6")}>
      <div className={cn("flex flex-col gap-2", compact ? "" : "grow")}>
        <SummaryTile badge="Адрес" value={displayName} />
        <SummaryTile
          badge="Размер бокса"
          value={unitType ? formatSurface(unitType.surface) : "—"}
        />
        {startedAt ? (
          <SummaryTile badge="Дата заезда" value={formatRentMoveInDate(startedAt)} />
        ) : null}
        {period && totalPrice != null ? (
          <SummaryTile
            badge="Стоимость аренды"
            value={formatAmount(totalPrice)}
            secondaryValue={hasDiscount ? formatAmount(totalBefore!) : undefined}
          />
        ) : null}
      </div>

      {footer ? <div className="mt-auto pt-6">{footer}</div> : null}
    </div>
  )
}
